// One workroom per (World, workroomKey). The room-rail launcher requests
// a fresh session on each click; other entry points may address the latest.
// Both revalidate admission through the room door before navigating or
// opening a session. History stays World-held under the same workroom.
import {
  rootSessionsOfRoom,
  WorldRoomIndex,
  WorldRoomEntry,
  type WorldRoomDirectory,
  type WorldRoomProjection,
} from "@cleo/contracts";

import * as Schema from "effect/Schema";

import { useBoardDeckStore } from "../boardDeckStore";
import { boardRegistrySnapshot, ensureBoardRegistryRead } from "./boardRegistryClient";
import { randomUUID } from "../lib/utils";
import { deckDefaultBoardKind } from "./workroomLaunch";
import { requestDeckFront, requestMemberDeckFront, withdrawDeckFront } from "./boardDeckSync";
import {
  openWorldSession,
  openWorldRoomEntry,
  openWorldSessionEntry,
  readWorldRoomEntry,
  readWorldRoomIndex,
  type WorldSessionClientOutcome,
} from "./worldSessionClient";

export type WorkroomDoorOutcome =
  | {
      readonly ok: true;
      readonly roomId: string;
      readonly sessionId: string;
      readonly created: boolean;
    }
  | { readonly ok: false; readonly kind: string; readonly reason: string };

/** The World's workroom for this key, when it stands (0..1 per World and key). */
/** The existing workroom a refused second birth names: "front room 'R_…' instead of opening another". */
const NAMED_WORKROOM = /\bfront room '(R_[A-Za-z0-9._:-]{1,253})' instead of opening another\b/;
export function existingWorkroomNamed(reason: string): string | null {
  return NAMED_WORKROOM.exec(reason)?.[1] ?? null;
}

export function findWorkroom(
  index: WorldRoomDirectory,
  workroomKey: string,
): WorldRoomProjection | undefined {
  return index.rooms.find((room) => room.kind === "workroom" && room.workroomKey === workroomKey);
}

/** The session a room's chip addresses: its most recently born root session. */
export function workroomEntrySessionId(index: WorldRoomDirectory, roomId: string): string | null {
  const roots = rootSessionsOfRoom(index, roomId);
  return roots.length === 0 ? null : (roots[roots.length - 1]?.sessionId ?? null);
}

/** The same minting the strip uses for a session id (client-minted, door-verified). */
function mintId(prefix: "R" | "S"): string {
  return `${prefix}_${randomUUID().replaceAll("-", "")}`;
}

/**
 * A workroom opens WITH its opening board (SB15, RM-CAN-09): every door
 * that addresses a workroom session fronts the board in that session's
 * deck before navigating. The deck's one-time default
 * (ensureDeckDefaultSurface) seats only a never-initialized deck, so a board
 * tab the person closed earlier would otherwise never return on any later
 * entry — the room opened with nothing but its inbox seat (witnessed live in
 * the Dev Desk, 2026-09-17, on the dock chip and the palette).
 * A board door always fronts its own board (W-eebfbd47, 5 Oct 2026): pressing
 * Calendar showed the Inbox the session had last fronted. The door now fronts
 * the board every time; the session's other tabs stay where they are. A room
 * whose opening board resolves to Goals (the Goals room itself, or a workroom
 * with no single board, such as Boomgik!'s projects) keeps the deck's own
 * default, so it never fronts Goals over that workroom's own surfaces.
 */
export async function frontOpeningBoard(
  worldKey: string,
  sessionId: string,
  workroomKey: string,
  stillCurrent: () => boolean = () => true,
): Promise<boolean> {
  // The registry is a display cache. A successful room entry must resolve the
  // current opening reference, including edits made in another window.
  await ensureBoardRegistryRead(worldKey);
  if (!stillCurrent()) return false;
  const registry = boardRegistrySnapshot(worldKey);
  if (registry === null || registry.worldKey !== worldKey) return false;
  const row =
    registry?.worldKey === worldKey
      ? registry.boards.find((entry) => entry.key === workroomKey)
      : undefined;
  if (row?.openingCanvas) {
    // The room selects a saved artifact, not another permission. MemberBoard
    // reads through the ordinary Canvas door, including revocation and bound
    // data checks; an unavailable Canvas never falls back to a different one.
    if (!row.launchable) return false;
    const deck = useBoardDeckStore.getState();
    // Use the same default initialization as every builtin Workroom, then
    // front its authored Canvas. Later session initialization sees a settled
    // deck and cannot replace this Canvas with the compatibility CRM board.
    deck.ensureDeckDefaultSurface(
      { worldKey, sessionId },
      deckDefaultBoardKind(workroomKey, worldKey),
    );
    requestMemberDeckFront(
      { worldKey, sessionId },
      {
        memberId: "world-vfs:canvas/" + row.openingCanvas.canvasId,
        memberType: "canvas",
        worldKey,
        label: row.title,
      },
    );
    return true;
  }
  withdrawDeckFront();
  const kind = deckDefaultBoardKind(workroomKey, worldKey);
  if (kind === "goals") return true;
  const deck = useBoardDeckStore.getState();
  deck.ensureDeckDefaultSurface({ worldKey, sessionId }, kind);
  // An explicit room opening must also survive the session’s late saved-tab read.
  requestDeckFront({ worldKey, sessionId }, kind);
  return true;
}

const decodeIndex = Schema.decodeUnknownSync(WorldRoomIndex);

function checkedReadback(
  outcome: WorldSessionClientOutcome,
  worldKey: string,
): WorldSessionClientOutcome {
  if (!outcome.ok) return outcome;
  try {
    const index = decodeIndex(outcome.index);
    if (index.worldKey === worldKey) return { ...outcome, index };
  } catch {
    /* A malformed projection cannot authorize navigation. */
  }
  return {
    ok: false,
    kind: "invalid_room",
    reason: "The World returned an invalid room index or a different World.",
  };
}

export async function activateOrCreateWorkroom(input: {
  readonly worldKey: string;
  /** A rendering snapshot only; the mutation door revalidates current admission. */
  readonly index: WorldRoomDirectory;
  readonly workroomKey: string;
  readonly title: string;
  readonly acceptReadback: (index: WorldRoomIndex) => boolean | void;
  readonly acceptEntryReadback?: (entry: WorldRoomEntry) => boolean | void;
  readonly openRoomEntry?: typeof openWorldRoomEntry;
  readonly openSessionEntry?: typeof openWorldSessionEntry;
  readonly readEntry?: typeof readWorldRoomEntry;
  readonly navigate: (target: { readonly sessionId: string }) => void | Promise<void>;
  /** The room-rail launcher starts a new scope on every deliberate click. */
  readonly freshSession?: boolean;
  readonly openSession?: typeof openWorldSession;
  readonly openRoom?: typeof openWorldRoomEntry;
  readonly readRooms?: typeof readWorldRoomIndex;
}): Promise<WorkroomDoorOutcome> {
  if (input.acceptEntryReadback) {
    const finishEntry = async (
      outcome: Awaited<ReturnType<typeof openWorldRoomEntry>>,
      roomId: string,
      sessionId: string,
    ): Promise<WorkroomDoorOutcome> => {
      if (!outcome.ok) return outcome;
      let entry: WorldRoomEntry;
      try {
        entry = Schema.decodeUnknownSync(WorldRoomEntry)(outcome.entry);
      } catch {
        return {
          ok: false,
          kind: "invalid_room",
          reason: "The World returned an invalid room entry.",
        };
      }
      if (
        entry.worldKey !== input.worldKey ||
        entry.room.roomId !== roomId ||
        entry.room.kind !== "workroom" ||
        entry.room.workroomKey !== input.workroomKey ||
        entry.session.sessionId !== sessionId ||
        entry.session.parentId !== roomId
      )
        return {
          ok: false,
          kind: "invalid_room",
          reason: "The requested workroom session did not read back exactly.",
        };
      if (input.acceptEntryReadback!(entry) === false)
        return {
          ok: false,
          kind: "context_changed",
          reason: "The active World or sign-in changed while opening the room.",
        };
      const current = () => input.acceptEntryReadback!(entry) !== false;
      const fronted = await frontOpeningBoard(
        input.worldKey,
        sessionId,
        input.workroomKey,
        current,
      );
      const unchanged = current();
      if (!fronted || !unchanged)
        return {
          ok: false,
          kind: unchanged ? "opening_unavailable" : "context_changed",
          reason: unchanged
            ? "The Workroom's opening content is unavailable. Try opening it again."
            : "The active World or sign-in changed while opening the room.",
        };
      await input.navigate({ sessionId });
      return { ok: true, roomId, sessionId, created: outcome.created };
    };
    const sessionId = mintId("S");
    const enterExisting = async (existing: {
      readonly roomId: string;
    }): Promise<WorkroomDoorOutcome> => {
      if (!input.freshSession) {
        const latest = await (input.readEntry ?? readWorldRoomEntry)({
          worldKey: input.worldKey,
          roomId: existing.roomId,
          latestRoot: true,
        });
        if (!latest.ok) return latest;
        const current = latest.entry;
        if (
          current.worldKey !== input.worldKey ||
          current.room.roomId !== existing.roomId ||
          current.room.kind !== "workroom" ||
          current.room.workroomKey !== input.workroomKey ||
          current.session.parentId !== existing.roomId
        )
          return {
            ok: false,
            kind: "invalid_room",
            reason: "The World did not confirm this workroom's current session.",
          };
        // The latest-entry query is the current admission door. Existing
        // forked or retired sessions are read as they stand, never reborn.
        return finishEntry(latest, existing.roomId, current.session.sessionId);
      }
      // The session door rechecks the owning workroom's current entitlement.
      const opened = await (input.openSessionEntry ?? openWorldSessionEntry)({
        worldKey: input.worldKey,
        parentId: existing.roomId,
        sessionId,
        label: input.title,
      });
      return finishEntry(opened, existing.roomId, sessionId);
    };
    const existing = findWorkroom(input.index, input.workroomKey);
    if (existing) return enterExisting(existing);
    const roomId = mintId("R");
    const opened = await (input.openRoomEntry ?? openWorldRoomEntry)({
      worldKey: input.worldKey,
      roomId,
      sessionId,
      label: input.title,
      kind: "workroom",
      workroomKey: input.workroomKey,
    });
    if (opened.ok) return finishEntry(opened, roomId, sessionId);
    if (opened.kind !== "upstream_refusal" && opened.kind !== "transport") return opened;
    // W-e978d3d2: before the Desk has read every room, the workroom may exist
    // unseen. The World refuses a second one and names it (the move law's next
    // step); front that room at once. The name is only where to look: the exact
    // latest-root read below is the admission, and it confirms the room is this
    // key's workroom before anything is shown.
    const named = opened.kind === "upstream_refusal" ? existingWorkroomNamed(opened.reason) : null;
    if (named !== null && named !== roomId) return enterExisting({ roomId: named });
    // A lost response or concurrent first birth reconciles once. Current
    // admission is still checked by the exact opening door on the retry.
    const readback = checkedReadback(
      await (input.readRooms ?? readWorldRoomIndex)(input.worldKey),
      input.worldKey,
    );
    if (!readback.ok) return opened;
    const concurrent = findWorkroom(readback.index, input.workroomKey);
    if (!concurrent) return opened;
    if (concurrent.roomId === roomId) {
      const recovered = await (input.openRoomEntry ?? openWorldRoomEntry)({
        worldKey: input.worldKey,
        roomId,
        sessionId,
        label: input.title,
        kind: "workroom",
        workroomKey: input.workroomKey,
      });
      return finishEntry(recovered, roomId, sessionId);
    }
    return enterExisting(concurrent);
  }
  const read = async (): Promise<WorldSessionClientOutcome> => {
    try {
      return checkedReadback(
        await (input.readRooms ?? readWorldRoomIndex)(input.worldKey),
        input.worldKey,
      );
    } catch {
      return {
        ok: false,
        kind: "transport",
        reason: "The authoritative room index could not be read.",
      };
    }
  };
  // The room door confirms only the room entry it opened; the directory this
  // path renders comes from the paged read, never from one whole-World call.
  const open = async (
    request: Parameters<typeof openWorldRoomEntry>[0],
  ): Promise<WorldSessionClientOutcome> => {
    const opened = await (input.openRoom ?? openWorldRoomEntry)(request);
    if (!opened.ok) return opened;
    const directory = await read();
    return directory.ok ? { ...directory, created: opened.created } : directory;
  };
  const finish = async (
    outcome: WorldSessionClientOutcome,
    roomId: string,
    originalSessionId: string,
    created: boolean,
  ): Promise<WorkroomDoorOutcome> => {
    const checked = checkedReadback(outcome, input.worldKey);
    if (!checked.ok) return checked;
    const room = findWorkroom(checked.index, input.workroomKey);
    const original = checked.index.sessions.find(
      (session) => session.sessionId === originalSessionId,
    );
    const sessionId = input.freshSession
      ? originalSessionId
      : workroomEntrySessionId(checked.index, roomId);
    if (
      room?.roomId !== roomId ||
      original?.parentId !== roomId ||
      original.roomId !== roomId ||
      sessionId === null
    )
      return {
        ok: false,
        kind: "invalid_room",
        reason: "The admitted workroom has no exact readable opening session.",
      };
    const current = () => input.acceptReadback(checked.index) !== false;
    const fronted =
      current() && (await frontOpeningBoard(input.worldKey, sessionId, input.workroomKey, current));
    const unchanged = current();
    if (!fronted || !unchanged)
      return {
        ok: false,
        kind: unchanged ? "opening_unavailable" : "context_changed",
        reason: unchanged
          ? "The Workroom's opening content is unavailable. Try opening it again."
          : "The active World or sign-in changed while opening the room.",
      };
    await input.navigate({ sessionId });
    return { ok: true, roomId, sessionId, created };
  };
  const reenter = async (
    index: WorldRoomIndex,
    room: WorldRoomProjection,
    recoveredSessionId?: string,
  ): Promise<WorkroomDoorOutcome> => {
    // Admission uses the original root; successful readback chooses the latest
    // root. A cached room or a new read alone is never launch authority.
    const original = rootSessionsOfRoom(index, room.roomId)[0];
    if (original === undefined)
      return {
        ok: false,
        kind: "invalid_room",
        reason: "This workroom has no authoritative opening session.",
      };
    const outcome = await open({
      worldKey: input.worldKey,
      roomId: room.roomId,
      sessionId: original.sessionId,
      label: room.label,
      kind: "workroom",
      workroomKey: input.workroomKey,
    });
    if (!outcome.ok) {
      void ensureBoardRegistryRead(input.worldKey);
      return outcome;
    }
    if (recoveredSessionId !== undefined)
      return finish(outcome, room.roomId, recoveredSessionId, true);
    if (!input.freshSession) return finish(outcome, room.roomId, original.sessionId, false);
    const admitted = checkedReadback(outcome, input.worldKey);
    if (!admitted.ok) return admitted;
    if (findWorkroom(admitted.index, input.workroomKey)?.roomId !== room.roomId)
      return { ok: false, kind: "invalid_room", reason: "The workroom changed during launch." };
    const sessionId = mintId("S");
    const opened = await (input.openSession ?? openWorldSession)({
      worldKey: input.worldKey,
      parentId: room.roomId,
      sessionId,
      label: input.title,
    });
    if (!opened.ok) return opened;
    // The door confirms only the new entry; the directory this path renders
    // comes from the paged read, never from one whole-World call.
    return finish(await read(), room.roomId, sessionId, true);
  };

  const fresh = await read();
  if (!fresh.ok) return fresh;
  const existing = findWorkroom(fresh.index, input.workroomKey);
  if (existing !== undefined) return reenter(fresh.index, existing);

  const roomId = mintId("R");
  const sessionId = mintId("S");
  const outcome = await open({
    worldKey: input.worldKey,
    roomId,
    sessionId,
    label: input.title,
    kind: "workroom",
    workroomKey: input.workroomKey,
  });
  if (outcome.ok) return finish(outcome, roomId, sessionId, outcome.created ?? true);
  void ensureBoardRegistryRead(input.worldKey);
  // Another host may have won the 0..1 workroom birth, or this host may have
  // lost the successful response. Reconcile at most once from new World state;
  // never classify authority by human error text or retry named grant refusals.
  if (outcome.kind === "upstream_refusal" || outcome.kind === "transport") {
    const after = await read();
    if (after.ok) {
      const concurrent = findWorkroom(after.index, input.workroomKey);
      if (concurrent !== undefined) {
        // Preserve the current admission refusal if access changed during birth.
        return reenter(
          after.index,
          concurrent,
          input.freshSession && concurrent.roomId === roomId ? sessionId : undefined,
        );
      }
    }
  }
  return outcome;
}
